"use client";

import { useUser } from "@clerk/react";
import { type FC, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { useWatchlistSync } from "~/feature/watchlist/hooks/useWatchlistSync";
import { useWatchlist } from "~/feature/watchlist/providers/WatchlistProvider";
import { clearWatchlistStorage } from "~/feature/watchlist/utils/storage";
import { useGetUserWatchlistQuery } from "~/feature/watchlist/watchlistApi";
import { api } from "~/store/api";
import type { AppDispatch } from "~/store/store";
import type { IWatchlistMetadata } from "~/types/watchlist";
import { handleError } from "~/utils/handleError";

interface IInitWatchlistProps {
  userMetadata: IWatchlistMetadata | null;
  clearGuestStorage: () => void;
}

export const InitWatchlist: FC<IInitWatchlistProps> = ({
  userMetadata,
  clearGuestStorage,
}) => {
  const { setUserWatchlist, setLoading } = useWatchlist();
  const { isSignedIn, isLoaded } = useUser();
  const dispatch = useDispatch<AppDispatch>();
  const wasSignedInRef = useRef(false);

  const { data, isLoading, error } = useGetUserWatchlistQuery(undefined, {
    skip: !isSignedIn || !isLoaded,
  });

  useEffect(() => {
    if (data) {
      setUserWatchlist(data.ids, data.metadata);
    }
  }, [data, setUserWatchlist]);

  useEffect(() => setLoading(isLoading), [isLoading, setLoading]);

  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);

  const handleSynced = useCallback(
    (ids: string[], metadata: IWatchlistMetadata) => {
      setUserWatchlist(ids, metadata);
    },
    [setUserWatchlist]
  );

  const handleCleanup = useCallback(() => {
    clearWatchlistStorage();
    clearGuestStorage();
  }, [clearGuestStorage]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (isSignedIn) {
      wasSignedInRef.current = true;
      return;
    }

    if (!wasSignedInRef.current) {
      return;
    }

    wasSignedInRef.current = false;
    dispatch(api.util.resetApiState());
    handleCleanup();
  }, [dispatch, handleCleanup, isLoaded, isSignedIn]);

  useWatchlistSync({
    isReady: !!isSignedIn && isLoaded && !!data,
    metadata: userMetadata,
    onSynced: handleSynced,
    onCleanup: handleCleanup,
  });

  return null;
};
